<script setup lang="ts">
import {computed, onMounted, ref} from 'vue'
import {useStore} from 'vuex'
import {Diamond} from '@/store/modules/diamonds'
import DiamondNav from '@/components/diamonds/DiamondNav.vue'
import DiamondImages from '@/components/diamonds/details/DiamondImages.vue'
import DiamondInfo from '@/components/diamonds/details/DiamondInfo.vue'
import DiamondDetailCarat from '@/components/diamonds/details/DiamondDetailCarat.vue'
import DiamondDetailColor from '@/components/diamonds/details/DiamondDetailColor.vue'
import DiamondDetailClarity from '@/components/diamonds/details/DiamondDetailClarity.vue'
import DiamondDetailCut from '@/components/diamonds/details/DiamondDetailCut.vue'
import DiamondDetailFluorescence from '@/components/diamonds/details/DiamondDetailFluorescence.vue'

const props = defineProps<{
  diamondId: string
}>()

const store = useStore()

const diamond = computed<Diamond | null>(() => store.getters['diamonds/getDiamondById'](props.diamondId))
const imageIdx = ref<string>("-1")
const host = process.env.VUE_APP_HOST || "";

const getFormName = (form: string) => {
  const forms: Record<string, string> = {
    BR: 'Brilliant',
    PR: 'Princess',
    CU: 'Cushion',
    OV: 'Oval',
    PS: 'Pear',
    EM: 'Emerald',
    MQ: 'Marquise',
    RA: 'Radiant',
    HS: 'Heart',
    TR: 'Trillion',
  }
  return forms[form] || form
}

const diamondTitle = computed(() => {
  if (!diamond.value) return ''
  return `${getFormName(diamond.value.form)} ${diamond.value.size.toLocaleString('de-DE')} ct.`
})

const fetchDiamond = async () => {
  await store.dispatch('diamonds/fetchDiamond', props.diamondId)
}

onMounted(() => {
  fetchDiamond()
})
</script>

<template>
  <div v-if="diamond" class="w-full mx-auto pb-12 font-light">
    <diamond-nav :title="diamondTitle"/>
    <div class="">
      <div class="px-4 sm:px-6 lg:px-8">
        <div class="lg:flex gap-12 items-start">
          <!-- Left side: Images Grid -->
          <div class="lg:w-2/3 sticky z-30 top-0">
            <DiamondImages
                class="sticky z-40 top-0 bg-white aspect-square md:aspect-auto md:h-auto"
                :certificate="diamond.certificate"
                :diamond="diamond"
                :image-idx="imageIdx"
                @switchImage="(event: any) => imageIdx = event"
            />
            <div class="bg-white md:hidden border-b border-t border-gray-200 py-2 px-4 flex justify-center">
              <div>
                <ul class="flex">
                  <li @click="imageIdx = '-1'" class="py-2 px-1">
                    <div
                        class="border-gray-900 rounded-full w-3 h-3 border"
                        :class="{
                'bg-gray-900': imageIdx == '-1',
                  }"
                    ></div>
                  </li>
                  <li v-if="diamond.image_url" @click="imageIdx = '0'" class="py-2 px-1">
                    <div
                        class="border-gray-900 rounded-full w-3 h-3 border"
                        :class="{
                'bg-gray-900': imageIdx == '0',
                  }"
                    ></div>
                  </li>
                  <li v-if="false" @click="imageIdx = '1'" class="py-2 px-1">
                    <div
                        class="border-gray-900 rounded-full w-3 h-3 border"
                        :class="{
                'bg-gray-900': imageIdx == '1',
                  }"
                    ></div>
                  </li>
                  <li @click="imageIdx = '2'" class="py-2 px-1">
                    <div
                        class="border-gray-900 rounded-full w-3 h-3 border"
                        :class="{
                'bg-gray-900': imageIdx == '2',
                  }"
                    ></div>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Right side: Diamond Info -->
          <DiamondInfo class="lg:w-1/3" :diamond="diamond"/>
        </div>
      </div>
      <!-- Diamond Details Sections -->
      <div class="mt-20 bg-brand-gray w-full">
        <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-10">
          <h3 class="text-4xl font-ivymode font-minerva">{{ $t("components.diamonds.details.fourCs.title") }}</h3>
        </div>
        <diamond-detail-carat
            class="py-8"
            :carat="diamond.size"
            :form="diamond.form"
            :width="diamond.width"
            :length="diamond.length"
        />
        <diamond-detail-color
            class="py-8"
            :color="diamond.color"
            :colored="diamond.colored"
        />
        <diamond-detail-clarity class="py-8" :clarity="diamond.clarity"/>
        <diamond-detail-cut
            v-if="diamond.form == 'BR'"
            class="py-8"
            :cut="diamond.cut"
            :stone-type="diamond.type"
        />
        <diamond-detail-fluorescence
            class="py-8"
            :fluorescence="diamond.fluorescence"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>

.font-serif {
  font-family: "Ivy Mode", serif;
}
</style>